import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";

// ==================== COUNTRY THUNKS ====================

export const createCountry = createAsyncThunk(
  "location/createCountry",
  async ({ name, code }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/location/create-country`, {
        name,
        code,
      });
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to create country",
      );
    }
  },
);

export const fetchAllCountries = createAsyncThunk(
  "location/fetchAllCountries",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/location/get-all-countries`);
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch countries",
      );
    }
  },
);

export const fetchCountryById = createAsyncThunk(
  "location/fetchCountryById",
  async (countryId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/location/get-country/${countryId}`);
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch country",
      );
    }
  },
);

export const updateCountry = createAsyncThunk(
  "location/updateCountry",
  async ({ countryId, name, code }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/location/update-country/${countryId}`, {
        name,
        code,
      });
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update country",
      );
    }
  },
);

export const deleteCountry = createAsyncThunk(
  "location/deleteCountry",
  async (countryId, { rejectWithValue }) => {
    try {
      await api.delete(`/location/delete-country/${countryId}`);
      return countryId;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete country",
      );
    }
  },
);

// ==================== COUNTY THUNKS ====================

export const createCounty = createAsyncThunk(
  "location/createCounty",
  async ({ countryId, name, delivery }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/location/create-county/${countryId}`, {
        name,
        delivery,
      });
      return { countryId, county: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to create county",
      );
    }
  },
);

export const fetchCounties = createAsyncThunk(
  "location/fetchCounties",
  async (countryId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/location/get-counties/${countryId}`);
      return { countryId, counties: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch counties",
      );
    }
  },
);

export const fetchCountyById = createAsyncThunk(
  "location/fetchCountyById",
  async ({ countryId, countyId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/location/get-county/${countryId}/${countyId}`,
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch county",
      );
    }
  },
);

export const updateCounty = createAsyncThunk(
  "location/updateCounty",
  async ({ countryId, countyId, name, delivery }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(
        `/location/update-county/${countryId}/${countyId}`,
        { name, delivery },
      );
      return { countryId, countyId, county: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update county",
      );
    }
  },
);

export const deleteCounty = createAsyncThunk(
  "location/deleteCounty",
  async ({ countryId, countyId }, { rejectWithValue }) => {
    try {
      await api.delete(`/location/delete-county/${countryId}/${countyId}`);
      return { countryId, countyId };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete county",
      );
    }
  },
);

// ==================== TOWN THUNKS ====================

export const createTown = createAsyncThunk(
  "location/createTown",
  async ({ countryId, countyId, name, delivery }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/location/create-town/${countryId}/${countyId}`,
        { name, delivery },
      );
      return { countryId, countyId, town: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to create town",
      );
    }
  },
);

export const fetchTowns = createAsyncThunk(
  "location/fetchTowns",
  async ({ countryId, countyId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/location/get-towns/${countryId}/${countyId}`,
      );
      return { countryId, countyId, towns: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch towns",
      );
    }
  },
);

export const fetchTownById = createAsyncThunk(
  "location/fetchTownById",
  async ({ countryId, countyId, townId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/location/get-town/${countryId}/${countyId}/${townId}`,
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch town",
      );
    }
  },
);

export const updateTown = createAsyncThunk(
  "location/updateTown",
  async (
    { countryId, countyId, townId, name, delivery },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.put(
        `/location/update-town/${countryId}/${countyId}/${townId}`,
        { name, delivery },
      );
      return { countryId, countyId, townId, town: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update town",
      );
    }
  },
);

export const deleteTown = createAsyncThunk(
  "location/deleteTown",
  async ({ countryId, countyId, townId }, { rejectWithValue }) => {
    try {
      await api.delete(
        `/location/delete-town/${countryId}/${countyId}/${townId}`,
      );
      return { countryId, countyId, townId };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete town",
      );
    }
  },
);

// ==================== DELIVERY THUNKS ====================

export const updateCountyDelivery = createAsyncThunk(
  "location/updateCountyDelivery",
  async ({ countryId, countyId, express, standard }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(
        `/location/update-county-delivery/${countryId}/${countyId}`,
        { express, standard },
      );
      return { countryId, countyId, delivery: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update delivery",
      );
    }
  },
);

export const updateTownDelivery = createAsyncThunk(
  "location/updateTownDelivery",
  async (
    { countryId, countyId, townId, express, standard },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.put(
        `/location/update-town-delivery/${countryId}/${countyId}/${townId}`,
        { express, standard },
      );
      return { countryId, countyId, townId, delivery: data.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update delivery",
      );
    }
  },
);
// ==================== SLICE ====================

const initialState = {
  countries: [],
  currentCountry: null,
  counties: [],
  currentCounty: null,
  towns: [],
  currentTown: null,
  loading: false,
  error: null,
  success: false,
  message: null,
};

const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.message = null;
    },
    setCurrentCountry: (state, action) => {
      state.currentCountry = action.payload;
      state.counties = action.payload?.counties || [];
    },
    setCurrentCounty: (state, action) => {
      state.currentCounty = action.payload;
      state.towns = action.payload?.towns || [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Countries ──
      .addCase(fetchAllCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload;
      })
      .addCase(fetchCountryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCountry = action.payload;
        state.counties = action.payload.counties || [];
      })
      .addCase(createCountry.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Country created successfully";
        state.countries.push(action.payload);
      })
      .addCase(updateCountry.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Country updated successfully";
        const idx = state.countries.findIndex(
          (c) => c._id === action.payload._id,
        );
        if (idx !== -1) state.countries[idx] = action.payload;
        if (state.currentCountry?._id === action.payload._id) {
          state.currentCountry = action.payload;
        }
      })
      .addCase(deleteCountry.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Country deleted successfully";
        state.countries = state.countries.filter(
          (c) => c._id !== action.payload,
        );
        if (state.currentCountry?._id === action.payload) {
          state.currentCountry = null;
          state.counties = [];
        }
      })

      // ── Counties ──
      .addCase(fetchCounties.fulfilled, (state, action) => {
        state.loading = false;
        state.counties = action.payload.counties;
      })
      .addCase(createCounty.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "County created successfully";
        const country = state.countries.find(
          (c) => c._id === action.payload.countryId,
        );
        if (country) country.counties.push(action.payload.county);
        if (state.currentCountry?._id === action.payload.countryId) {
          state.counties.push(action.payload.county);
        }
      })
      .addCase(updateCounty.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "County updated successfully";
        const country = state.countries.find(
          (c) => c._id === action.payload.countryId,
        );
        if (country) {
          const idx = country.counties.findIndex(
            (c) => c._id === action.payload.countyId,
          );
          if (idx !== -1) country.counties[idx] = action.payload.county;
        }
        const cIdx = state.counties.findIndex(
          (c) => c._id === action.payload.countyId,
        );
        if (cIdx !== -1) state.counties[cIdx] = action.payload.county;
      })
      .addCase(deleteCounty.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "County deleted successfully";
        const country = state.countries.find(
          (c) => c._id === action.payload.countryId,
        );
        if (country) {
          country.counties = country.counties.filter(
            (c) => c._id !== action.payload.countyId,
          );
        }
        state.counties = state.counties.filter(
          (c) => c._id !== action.payload.countyId,
        );
        if (state.currentCounty?._id === action.payload.countyId) {
          state.currentCounty = null;
          state.towns = [];
        }
      })

      // ── Towns ──
      .addCase(fetchTowns.fulfilled, (state, action) => {
        state.loading = false;
        state.towns = action.payload.towns;
      })
      .addCase(createTown.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Town created successfully";
        const county = state.counties.find(
          (c) => c._id === action.payload.countyId,
        );
        if (county) county.towns.push(action.payload.town);
        if (state.currentCounty?._id === action.payload.countyId) {
          state.towns.push(action.payload.town);
        }
      })
      .addCase(updateTown.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Town updated successfully";
        const county = state.counties.find(
          (c) => c._id === action.payload.countyId,
        );
        if (county) {
          const idx = county.towns.findIndex(
            (t) => t._id === action.payload.townId,
          );
          if (idx !== -1) county.towns[idx] = action.payload.town;
        }
        const tIdx = state.towns.findIndex(
          (t) => t._id === action.payload.townId,
        );
        if (tIdx !== -1) state.towns[tIdx] = action.payload.town;
      })
      .addCase(deleteTown.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Town deleted successfully";
        const county = state.counties.find(
          (c) => c._id === action.payload.countyId,
        );
        if (county) {
          county.towns = county.towns.filter(
            (t) => t._id !== action.payload.townId,
          );
        }
        state.towns = state.towns.filter(
          (t) => t._id !== action.payload.townId,
        );
      })

      // ── Delivery ──
      .addCase(updateCountyDelivery.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Delivery updated successfully";
        const county = state.counties.find(
          (c) => c._id === action.payload.countyId,
        );
        if (county) county.delivery = action.payload.delivery;
      })
      .addCase(updateTownDelivery.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Delivery updated successfully";
        const county = state.counties.find(
          (c) => c._id === action.payload.countyId,
        );
        if (county) {
          const town = county.towns.find(
            (t) => t._id === action.payload.townId,
          );
          if (town) town.delivery = action.payload.delivery;
        }
        const t = state.towns.find((t) => t._id === action.payload.townId);
        if (t) t.delivery = action.payload.delivery;
      })

      // ── Loading helpers (must come AFTER all addCase) ──
      .addMatcher(
        (action) =>
          action.type.startsWith("location/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
          state.success = false;
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("location/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
          state.success = false;
        },
      );
  },
});

export const {
  clearErrors,
  clearSuccess,
  setCurrentCountry,
  setCurrentCounty,
} = locationSlice.actions;

export default locationSlice.reducer;
